"use client";

import { Link } from "@/i18n/navigation";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

interface RelatedPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readingTime: number;
}

const RelatedPosts = ({
  posts,
  title,
  minReadLabel,
}: {
  posts: RelatedPost[];
  title: string;
  minReadLabel: string;
}) => {
  if (posts.length === 0) return null;

  const card = (rp: RelatedPost) => (
    <Link href={`/blog/${rp.slug}`} className="block h-full">
      <AnimatedCard
        className="rounded-[18px] lg:rounded-[22px] h-full"
        circleClassName="w-[120px] h-[120px] blur-[70px]"
      >
        <BorderWrapper
          wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
          className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] overflow-hidden relative flex flex-col"
        >
          <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
          <div className="flex flex-col flex-1 relative z-10">
            <div className="flex items-center gap-[12px] mb-[20px]">
              <p className="text-[12px] font-light text-foreground/35">{rp.date}</p>
              <span className="w-[3px] h-[3px] rounded-full bg-foreground/20" />
              <p className="text-[12px] font-light text-foreground/35">{rp.readingTime} {minReadLabel}</p>
            </div>
            <h3 className="text-[20px] lg:text-[24px] font-semibold leading-[1.25] text-white mb-[14px]">
              {rp.title}
            </h3>
            <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
              {rp.description}
            </p>
          </div>
        </BorderWrapper>
      </AnimatedCard>
    </Link>
  );

  return (
    <div className="mt-[60px] lg:mt-[80px]">
      <h2 className="text-[24px] lg:text-[32px] font-semibold leading-[1.2] text-white mb-[24px] lg:mb-[32px]">
        {title}
      </h2>

      {/* MOBILE + TABLET CAROUSEL */}
      <div className="lg:hidden">
        <Carousel
          aria-label={title}
          opts={{ align: "center", loop: false }}
          className="w-full [&>div]:!overflow-visible"
        >
          <CarouselContent className="-ml-2 md:-ml-[32px]">
            {posts.map((rp) => (
              <CarouselItem key={rp.slug} className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                {card(rp)}
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center items-center mt-[30px] gap-[15px]">
            <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
            <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
          </div>
        </Carousel>
      </div>

      {/* DESKTOP GRID */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-[16px]">
        {posts.map((rp) => (
          <div key={rp.slug}>{card(rp)}</div>
        ))}
      </div>
    </div>
  );
};

export default RelatedPosts;
